/* eslint-disable no-promise-executor-return */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-func-assign */
import axios from 'axios';
import Cookies from 'js-cookie';
import {
  encrypt,
  errorLogger,
  authStorage,
  transactionStorage,
  kycStorage,
  genericStorage,
} from '@ajaibid/utils';

import waitRefreshToken from '@helpers/fetcher/session/waitRefreshToken';
import {
  AES_ENCRYPTION_SECRET_KEY,
  COOKIE_KEY,
  COOKIE_SECURE,
  GENERIC_STORAGE_KEY,
} from '@config/constanta';
import { ClientJS } from 'clientjs';

const BASE_URL = process.env.API_URL;
const LOGOUTURL = process.env.LOGOUT_URL;

// Create a new ClientJS object
const client = new ClientJS();

// Get the client's fingerprint id
const fingerprint = client.getFingerprint();
const browserInfo = client.getBrowser();
const OSInfo = client.getOS();

const validatePin = `${BASE_URL}v4/users/me/pin/validate/`;
const createdPinV3 = `${BASE_URL}v3/users/me/pin/create/`;
const createdPinV2 = `${BASE_URL}v2/users/me/pin/create/`;

export const V1 = `${BASE_URL}v1/`;
export const V2 = `${BASE_URL}v2/`;
export const V3 = `${BASE_URL}v3/`;
export const V4 = `${BASE_URL}v4/`;
export const V5 = `${BASE_URL}v5/`;
export const V6 = `${BASE_URL}v6/`;
export const V7 = `${BASE_URL}v7/`;

window.originalFetch = window.fetch;
window.pendingFetch = 0;

// Disable to avoid error mocking fetch
if (process.env.NODE_ENV !== 'test') {
  window.fetch = async (...args) => {
    window.pendingFetch += 1;
    try {
      // eslint-disable-next-line no-undef
      const result = await window.originalFetch(...args);

      window.pendingFetch -= 1;

      return result;
    } catch (err) {
      window.pendingFetch -= 1;

      throw err;
    }
  };
}

export const clearSession = (shouldRedirectToLogout = true) => {
  Cookies.remove(COOKIE_KEY.refresh_token);
  Cookies.remove(COOKIE_KEY.access_token);
  Cookies.remove(COOKIE_KEY.pin_token);
  Cookies.remove(COOKIE_KEY.hashedHeader);
  Cookies.remove(COOKIE_KEY.redirect);

  genericStorage.remove('hide_influencer_banner');
  genericStorage.remove(GENERIC_STORAGE_KEY.layout);
  authStorage.clear();
  transactionStorage.clear();
  kycStorage.clear();

  if (shouldRedirectToLogout) {
    window.location.assign(LOGOUTURL);
  }
};

export function serializeQueryString(obj) {
  if (!obj) return '';
  return `?${Object.keys(obj)
    .reduce((a, k) => {
      if (Array.isArray(obj[k])) {
        for (const val of obj[k]) {
          a.push(`${k}[]=${encodeURIComponent(val)}`);
        }
      } else if (obj[k] !== null && obj[k] !== undefined) {
        a.push(`${k}=${encodeURIComponent(obj[k])}`);
      }
      return a;
    }, [])
    .join('&')}`;
}

const firstInit = parseInt(Cookies.get(COOKIE_KEY.isFirstLoad), 10);
const isFirstLoad = firstInit === 1;
let cachePinToken = '';
let cacheAccessToken = '';
let callRefreshPin = 0;
let callRefreshToken = 0;

const defineHeaders = async (usePinToken) => {
  const ACCESS_TOKEN = Cookies.get(COOKIE_KEY.access_token);
  const PIN_TOKEN = Cookies.get(COOKIE_KEY.pin_token);
  const OTP_TOKEN = Cookies.get(COOKIE_KEY.otp_token);

  const headerAuthorization = usePinToken ? `jwt ${PIN_TOKEN}` : `jwt ${ACCESS_TOKEN}`;

  return new Promise((resolve) => {
    const LANG = genericStorage.get('lang');
    const hashedEmail = Cookies.get(COOKIE_KEY.hashedHeader);
    const deviceSign = Cookies.get(COOKIE_KEY.deviceSign);
    const usedDeviceSign = deviceSign || fingerprint;

    const headers = {
      'Content-Type': 'application/json',
      'Accept-Language': 'id',
      'x-platform': 'WEB',
      'x-product': 'stock-mf',
      'x-device-name': `Web ${browserInfo || ''} (${OSInfo || ''})`,
      'x-device-signature': usedDeviceSign,
    };

    Cookies.set(COOKIE_KEY.deviceSign, usedDeviceSign, {
      ...COOKIE_SECURE,
      expires: 300,
    });

    if (hashedEmail) headers['X-Ht-Ver-Id'] = hashedEmail;
    if (headerAuthorization) headers.Authorization = headerAuthorization;
    if (OTP_TOKEN) headers.Authorization = `jwt ${OTP_TOKEN}`;
    if (LANG) headers['Accept-Language'] = LANG;
    resolve(headers);
  });
};

function parseJSON(response) {
  return new Promise((resolve, reject) =>
    response
      .text()
      .then((json) =>
        resolve({
          status: response.status,
          ok: response.ok,
          json: json || {},
        })
      )
      .catch((err) => {
        errorLogger('ERROR parseJSON', err.message);
        reject(err);
      })
  );
}

function RefreshToken(config) {
  const getRefreshToken = Cookies.get(COOKIE_KEY.refresh_token);

  const newConfig = {
    ...config,
    Authorization: `jwt ${getRefreshToken}`,
  };

  return new Promise((resolve, reject) => {
    axios
      .post(`${V7}refresh/`, {}, { headers: newConfig })
      .then((res) => {
        Cookies.set(COOKIE_KEY.access_token, res.data.access_token, COOKIE_SECURE);
        Cookies.set(COOKIE_KEY.refresh_token, res.data.refresh_token, COOKIE_SECURE);

        Cookies.set(COOKIE_KEY.redirect, window.location.pathname);
        if (res.status === 200) {
          resolve(res.data.access_token);
        } else {
          reject(res);
          clearSession();
        }
      })
      .catch((err) => {
        reject(err);
        errorLogger('ERROR_API', err.message, {
          path: `${V7}refresh/`,
          'X-Ht-Ver-Id': !!config['X-Ht-Ver-Id'],
        });
        clearSession();
      });
  });
}

function RefreshPin(config) {
  const getPinRefresh = Cookies.get(COOKIE_KEY.pin_refresh);

  const newConfig = {
    ...config,
    Authorization: `jwt ${getPinRefresh}`,
  };

  return new Promise((resolve, reject) => {
    axios
      .post(`${V7}refresh/pin`, {}, { headers: newConfig })
      .then((res) => {
        Cookies.set(COOKIE_KEY.pin_refresh, res.data.pin_refresh, COOKIE_SECURE);
        Cookies.set(COOKIE_KEY.pin_token, res.data.pin_token, COOKIE_SECURE);

        if (!isFirstLoad) {
          const ciphertext = encrypt(res.data.pin_token, AES_ENCRYPTION_SECRET_KEY);
          authStorage.set('xyz', ciphertext);
        }

        if (res.status === 200) {
          resolve(res.data.pin_token);
        } else {
          reject(res);
          clearSession();
        }
      })
      .catch((err) => {
        reject(err);
        errorLogger('ERROR_API', err.message, {
          path: `${V7}refresh/pin`,
          'X-Ht-Ver-Id': !!newConfig['X-Ht-Ver-Id'],
        });
        clearSession();
      });
  });
}

const orignalRequest = (
  uri,
  body,
  method = 'GET',
  isForm = false,
  usePinToken = false,
  customHeaders = {}
) =>
  new Promise((resolve, reject) => {
    let bodyRequest;
    if (isForm === true) {
      bodyRequest = new FormData();
      Object.keys(body).forEach((v) => bodyRequest.append(v, body[v]));
    } else {
      bodyRequest = body ? JSON.stringify(body) : body;
    }

    defineHeaders(usePinToken).then(async (headers) => {
      fetch(uri, {
        headers: {
          ...headers,
          ...customHeaders,
        },
        method,
        body: bodyRequest,
        usePinToken,
      })
        .then(parseJSON)
        .then((response) => {
          if (Object.keys(response.json).length !== 0) {
            response.json = JSON.parse(response.json);
          }
          if (response.status === 403) {
            errorLogger('ERROR_WAF', 'response', {
              path: uri,
              'X-Ht-Ver-Id': !!headers['X-Ht-Ver-Id'],
            });
          }
          resolve(response);
        })
        .catch((err) => {
          errorLogger('ERROR_API', err.message, {
            path: uri,
            'X-Ht-Ver-Id': !!headers['X-Ht-Ver-Id'],
          });
          let error = {
            message:
              'Sedang Ada Gangguan | Silakan coba lagi nanti, atau hubungi layanan pelanggan Ajaib',
          };

          if (uri.includes(validatePin) || !headers['X-Ht-Ver-Id']) {
            error = {
              message: 'Your session has expired',
            };
          }
          reject(error);
        });
    });
  });

const memorizePinToken = (headers) => {
  callRefreshPin += 1;
  return new Promise(async (resolve) => {
    if (callRefreshPin < 2 && !cachePinToken) {
      const newPinToken = await RefreshPin(headers);
      cachePinToken = newPinToken;
      resolve(newPinToken);
    } else {
      resolve(cachePinToken);
    }
    setInterval(() => {
      callRefreshPin = 0;
      cachePinToken = '';
    }, 10000);
  });
};

const memorizeAccessToken = (headers) => {
  callRefreshToken += 1;
  return new Promise(async (resolve) => {
    if (callRefreshToken < 2 && !cacheAccessToken) {
      const newAccessToken = await RefreshToken(headers);
      cacheAccessToken = newAccessToken;
      resolve(newAccessToken);
    } else {
      resolve(cacheAccessToken);
    }
    setInterval(() => {
      callRefreshToken = 0;
      cacheAccessToken = '';
    }, 10000);
  });
};

const Fetchers = async (
  uri,
  body,
  method = 'GET',
  isForm = false,
  usePinToken = false,
  customHeaders = {}
) => {
  const headers = await defineHeaders();
  const response = await orignalRequest(uri, body, method, isForm, usePinToken, customHeaders);

  try {
    if (!response.ok) {
      if (response.status === 403) {
        clearSession();
        return response;
      }
      if (response.status === 401 || response.status === 403) {
        if (usePinToken === false && !isFirstLoad) {
          const newToken = await memorizeAccessToken(headers);
          return await Fetchers(uri, body, method, isForm, usePinToken, {
            Authorization: `jwt ${newToken || undefined}`,
          });
        }

        if (usePinToken === true) {
          const newToken = await memorizePinToken(headers);
          return await Fetchers(uri, body, method, isForm, usePinToken, {
            Authorization: `jwt ${newToken || undefined}`,
          });
        }
      }
    }

    if (uri.includes(validatePin) && response.json.result.valid === true) {
      if (response.json.result.pin_token) {
        Cookies.set(COOKIE_KEY.pin_token, response.json.result.pin_token, COOKIE_SECURE);
      }
      Cookies.set(COOKIE_KEY.access_token, response.json.result.access_token, COOKIE_SECURE);
      Cookies.set(COOKIE_KEY.refresh_token, response.json.result.refresh_token, COOKIE_SECURE);
      Cookies.set(COOKIE_KEY.show_2fa, response.json.result.show_2fa, COOKIE_SECURE);
      Cookies.set(COOKIE_KEY.isFirstLoad, 0, COOKIE_SECURE);
    }

    if ((uri.includes(createdPinV3) || uri.includes(createdPinV2)) && response.json.access_token) {
      Cookies.set(COOKIE_KEY.access_token, response.json.access_token, COOKIE_SECURE);
      Cookies.set(COOKIE_KEY.refresh_token, response.json.refresh_token, COOKIE_SECURE);
    }

    return response;
  } catch (error) {
    return error;
  }
};

export default class Fetch {
  static async get(uri, usePinToken = false) {
    if (!usePinToken) {
      await waitRefreshToken();
    }
    return Fetchers(uri, undefined, 'GET', false, usePinToken);
  }

  static async post(uri, body, isForm = false, usePinToken = false, customHeaders = {}) {
    if (!usePinToken) {
      await waitRefreshToken();
    }
    return Fetchers(uri, body, 'POST', isForm, usePinToken, customHeaders);
  }

  static async patch(uri, body, isForm = false, condition = false) {
    await waitRefreshToken();
    return Fetchers(uri, body, 'PATCH', isForm, condition);
  }

  static async put(uri, body, isForm = false, condition = false) {
    await waitRefreshToken();
    return Fetchers(uri, body, 'PUT', isForm, condition);
  }

  static async delete(uri, body, isForm = false, condition = false) {
    await waitRefreshToken();
    return Fetchers(uri, body, 'DELETE', isForm, condition);
  }
}
